import { useCallback } from 'react';
import { Link, useNavigate } from '@tanstack/react-router';
import { Sparkles } from 'lucide-react';
import { useMargensEvolucao, useMargensItensAbaixo, useMargensOrcamentos, useMargensResumo } from '@/api/margens';
import { formatarContagem, formatarFracaoPercentual } from '@/lib/format';
import { useSessao } from '@/lib/session';
import { PontoEquilibrioCard } from '@/components/ponto-equilibrio';
import { Bloco } from './bloco';
import { BlocoCustoAtualizado } from './custo-atualizado';
import { CartoesMargens, RodapeMc } from './cartoes-margens';
import { EvolucaoMensal } from './evolucao-mensal';
import { FiltrosMargens } from './filtros-margens';
import { GraficoOrcamentos } from './grafico-orcamentos';
import { ListaItensAbaixo } from './lista-itens-abaixo';
import { SecaoGrupo } from './secao-grupo';
import { SecaoItensAbaixo } from './secao-itens-abaixo';
import { lerBusca, paraConsulta, ROTULO_TIPO, temFiltro, type BuscaMargens, type TipoItem } from './tipos';
import { BotaoRelatorios } from '@/relatorios';


const SECAO = 'rounded-2xl border border-border bg-card px-5 py-[18px]';

/** Tela "Painel de margens" (/precificar/margens). Todo agregado vem de GET /margens/*; a tela so formata e mostra. */
export function MargensPage({ busca }: { busca: BuscaMargens }) {
  const { empresaId } = useSessao();
  const navigate = useNavigate();
  const consulta = paraConsulta(busca);
  const resumo = useMargensResumo(consulta);
  const orcamentos = useMargensOrcamentos({ ...consulta, limit: 30 });
  const lateral = useMargensItensAbaixo({ ...consulta, nivel: 'meta', ordenar: 'lucro', direcao: 'asc', limit: 5 });
  const evolucao = useMargensEvolucao(consulta);

  const mudar = useCallback(
    (mudanca: Partial<Record<keyof BuscaMargens, string | undefined>>) => {
      void navigate({ to: '/precificar/margens', search: lerBusca({ ...busca, ...mudanca }), replace: true });
    },
    [navigate, busca],
  );
  const limpar = useCallback(() => void navigate({ to: '/precificar/margens', search: {}, replace: true }), [navigate]);

  if (empresaId === null) {
    return <p className="px-5 py-10 text-center text-small text-ink-muted">Escolha a empresa no menu lateral para ver as margens.</p>;
  }
  const filtrado = temFiltro(busca);
  // Sem nenhum orcamento no periodo (total da propria resposta de /margens/resumo) e sem filtro: um so estado vazio no lugar das secoes.
  const semOrcamentos = !filtrado && resumo.data?.orcamentos === 0;
  const periodo = { ...(busca.dataDe ? { dataDe: busca.dataDe } : {}), ...(busca.dataAte ? { dataAte: busca.dataAte } : {}) };

  return (
    <div className="flex flex-col gap-5 pb-4">
      <div className="flex flex-col gap-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <div className="text-small text-ink-muted">
              Precificar <span aria-hidden="true">/</span> Painel de margens
            </div>
            <h1 className="mt-1 text-display font-semibold tracking-[-0.01em]">Painel de margens</h1>
            <div className="mt-1 text-ink-secondary">Onde o lucro está escapando: orçamentos, itens e produtos abaixo da meta.</div>
          </div>
          <BotaoRelatorios tela="margens" params={consulta} />
        </div>
        <FiltrosMargens busca={busca} onBusca={mudar} onLimpar={limpar} />
      </div>

      <PontoEquilibrioCard largura="ampla" />

      {semOrcamentos ? (
        <div role="status" className="flex flex-col items-center gap-2 px-5 py-10 text-center text-small text-ink-muted">
          <p className="m-0">Ainda não há orçamentos aprovados no período. Crie ou aprove orçamentos para ver as margens aqui.</p>
          <Link to="/vender/orcamentos" className="font-medium text-primary hover:underline">
            Ir para Orçamentos
          </Link>
        </div>
      ) : (
        <>
        <CartoesMargens
          resumo={resumo.data}
          carregando={resumo.isFetching}
          erro={resumo.error ? resumo.error.message : null}
          onTentar={() => void resumo.refetch()}
          busca={busca}
        />

        <div className="flex flex-wrap items-start gap-5">
          <section aria-label="Lucro por orçamento" aria-busy={orcamentos.isFetching} className={`${SECAO} min-w-0 flex-[999_1_560px]`}>
            <h2 className="m-0 text-title font-semibold">Lucro por orçamento</h2>
            <div className="mb-3.5 text-small text-ink-muted">
              Ordenado do menor para o maior
              {orcamentos.data?.escala.minimo || orcamentos.data?.escala.meta
                ? ` · linhas em ${[orcamentos.data.escala.minimo, orcamentos.data.escala.meta].filter(Boolean).map((m) => `${formatarFracaoPercentual(m!.valor).replace(/,00%$/, '%')} (${m === orcamentos.data!.escala.minimo ? 'mínimo' : 'meta'})`).join(' e ')}`
                : ' · linhas de mínimo e meta de cada tabela'}
              {orcamentos.data && orcamentos.data.total > orcamentos.data.itens.length
                ? ` · os ${formatarContagem(orcamentos.data.itens.length)} de menor lucro, de ${formatarContagem(orcamentos.data.total)}`
                : ''}
            </div>
            <Bloco
              carregando={orcamentos.isFetching}
              erro={orcamentos.error}
              temDados={!!orcamentos.data}
              vazio={orcamentos.data?.itens.length === 0}
              mensagemVazia={filtrado ? 'Nenhum orçamento com lucro calculado nesses filtros.' : 'Ainda não há orçamentos aprovados ou em rascunho com lucro calculado.'}
              textoCarregando="Carregando orçamentos…"
              onTentar={() => void orcamentos.refetch()}
            >
              {orcamentos.data && <GraficoOrcamentos dados={orcamentos.data} />}
            </Bloco>
          </section>

          <aside className="flex min-w-0 flex-[1_1_300px] flex-col gap-4">
            <section aria-label="Itens negociados abaixo da meta (resumo)" className="rounded-2xl border border-border bg-card px-[18px] py-4" data-testid="lateral-itens-abaixo">
              <div className="mb-2.5 font-semibold">Itens negociados abaixo da meta</div>
              <Bloco
                carregando={lateral.isFetching}
                erro={lateral.error}
                temDados={!!lateral.data}
                vazio={lateral.data?.total === 0}
                mensagemVazia="Nenhum item negociado abaixo da meta."
                textoCarregando="Carregando itens…"
                onTentar={() => void lateral.refetch()}
              >
                {lateral.data && (
                  <>
                    <ListaItensAbaixo itens={lateral.data.itens} />
                    {lateral.data.total > lateral.data.itens.length && (
                      <a href="#itens-abaixo" className="mt-3 inline-block text-small font-medium text-primary hover:underline">
                        Ver os {formatarContagem(lateral.data.total)} itens
                      </a>
                    )}
                  </>
                )}
              </Bloco>
            </section>
            <section aria-label="Leitura do copiloto" className="rounded-2xl border border-border bg-card px-[18px] py-4" data-testid="copiloto">
              <div className="flex items-center gap-2 font-semibold">
                <Sparkles aria-hidden="true" className="size-[15px] text-primary" />
                Leitura do copiloto
              </div>
              <p role="status" className="mt-1.5 mb-0 text-small text-ink-body">
                Indisponível por enquanto: não há API do copiloto, então nenhuma leitura é gerada aqui. Os números ao lado vêm direto dos orçamentos gravados.
              </p>
            </section>
          </aside>
        </div>

        <SecaoItensAbaixo busca={busca} />

        <BlocoCustoAtualizado busca={busca} />

        <SecaoGrupo
          id="por-tabela"
          titulo="Por tabela de preço"
          descricao="Lucro médio ponderado pela receita. O nome abre a lista de preços da tabela."
          recorte="por-tabela"
          busca={busca}
          colunaRotulo="Tabela"
          colunaQuantidade="Orçamentos"
          rotulo={(g) => (
            <Link to="/precificar/lista" search={{ tabelaId: g.chave, ...(busca.tipo ? { tipo: busca.tipo } : {}) }} data-testid="drill-tabela" className="font-medium text-ink no-underline hover:underline">
              {g.rotulo}
            </Link>
          )}
          vazio="Nenhuma tabela com orçamentos nesses filtros."
        />
        <SecaoGrupo
          id="por-cliente"
          titulo="Por cliente"
          descricao="O nome abre os orçamentos do cliente."
          recorte="por-cliente"
          busca={busca}
          colunaRotulo="Cliente"
          colunaQuantidade="Orçamentos"
          rotulo={(g) =>
            g.chave === '' ? (
              <span className="text-ink-secondary">{g.rotulo}</span>
            ) : (
              <Link to="/vender/orcamentos" search={{ clienteId: g.chave, ...periodo, ...(busca.escopo === 'APROVADO' ? { status: 'APROVADO' as const } : {}) }} data-testid="drill-cliente" className="font-medium text-ink no-underline hover:underline">
                {g.rotulo}
              </Link>
            )
          }
          vazio="Nenhum cliente com orçamentos nesses filtros."
        />
        <SecaoGrupo
          id="por-produto"
          titulo="Por produto"
          descricao="Lucro médio das linhas do item, ponderado pela receita da linha. O nome abre o cadastro do item."
          recorte="por-produto"
          busca={busca}
          colunaRotulo="Produto"
          colunaQuantidade="Orçamentos"
          rotulo={(g) => (
            <Link to="/cadastros/itens/$id" params={{ id: g.chave }} data-testid="drill-produto" className="text-ink no-underline hover:underline">
              <span className="tabular font-medium">{g.codigo}</span> · {g.rotulo}
            </Link>
          )}
          vazio="Nenhum produto vendido nesses filtros."
        />
        <SecaoGrupo
          id="por-categoria"
          titulo="Por categoria"
          descricao="Lucro médio por categoria do cadastro de itens, com as subcategorias. Os itens sem categoria aparecem à parte."
          recorte="por-categoria"
          busca={busca}
          colunaRotulo="Categoria"
          colunaQuantidade="Orçamentos"
          rotulo={(g) => g.rotulo}
          vazio="Nenhuma categoria com orçamentos nesses filtros."
        />
        <SecaoGrupo
          id="por-tipo"
          titulo="Por tipo de item"
          descricao="Tipo do item (fabricado, mercadoria, serviço). O nome abre a lista de preços do tipo."
          recorte="por-tipo"
          busca={busca}
          colunaRotulo="Tipo"
          colunaQuantidade="Orçamentos"
          rotulo={(g) => (
            <Link to="/precificar/lista" search={{ tipo: g.chave as TipoItem, ...(busca.tabelaId ? { tabelaId: busca.tabelaId } : {}) }} data-testid="drill-tipo" className="font-medium text-ink no-underline hover:underline">
              {ROTULO_TIPO[g.chave as TipoItem] ?? g.rotulo}
            </Link>
          )}
          vazio="Nenhum tipo de item com orçamentos nesses filtros."
        />

        <section aria-label="Evolução mensal" aria-busy={evolucao.isFetching} className={SECAO} data-testid="secao-evolucao">
          <h2 className="m-0 text-title font-semibold">Evolução mensal</h2>
          <div className="mb-3.5 text-small text-ink-muted">Lucro médio por mês da data do orçamento (escala de 0 a 30%).</div>
          <Bloco
            carregando={evolucao.isFetching}
            erro={evolucao.error}
            temDados={!!evolucao.data}
            vazio={evolucao.data?.pontos.length === 0}
            mensagemVazia="Sem orçamentos no período para mostrar a evolução."
            textoCarregando="Carregando evolução…"
            onTentar={() => void evolucao.refetch()}
          >
            {evolucao.data && <EvolucaoMensal dados={evolucao.data} />}
          </Bloco>
        </section>

        <RodapeMc mc={resumo.data?.mc} />
        </>
      )}
    </div>
  );
}
